/**
 * CogoAgent — Floating chat icon + assistant-ui Thread panel.
 *
 * Refactored from monolithic 417-line component to layered architecture:
 *   - GeminiAgentRuntime (Plan 02) handles Gemini native function calling
 *   - AgentRuntimeProvider bridges runtime to assistant-ui
 *   - AgentPanel lazy-loads the Thread UI to avoid bundle bloat
 *   - EmbeddedToolProvider (Plan 03) derives app-scoped tools from blocks
 *
 * Embedded mode (D-18): When appId is provided, fetches agent_configs to
 * check embedded_enabled before rendering. FAB appears only on deployed
 * apps where embedded_enabled is true.
 *
 * Persona detection: auto-selects shopping/dashboard/generic based on
 * available tools (cart tools -> shopping, data-table tools -> dashboard).
 * If agent_configs provides a persona, that takes precedence.
 */

import { useState, useEffect, lazy, Suspense } from "react"
import { AgentRuntimeProvider, useAgentUI } from "./AgentRuntimeProvider"
import { EmbeddedToolProvider, useEmbeddedTools } from "./EmbeddedToolProvider"
import {
  shoppingPersona,
  dashboardPersona,
  genericPersona,
  partnerPersona,
  type AgentPersona,
  type LlmDriver,
} from "@cogo/webmcp-runtime"

const AgentPanel = lazy(() => import("./AgentPanel"))

export interface CogoAgentProps {
  /** Runtime credential marker. If no driver is configured, the agent is hidden. */
  apiKey?: string
  /** Explicit LLM driver. Production consumers should use an internal driver. */
  llmDriver?: LlmDriver
  /** App name shown in chat header */
  appName?: string
  /** Show/hide the agent. Default: true */
  enabled?: boolean
  /** App ID for fetching agent_configs. If set, enables embedded mode with config check. */
  appId?: string
}

/** Sparkles icon — matches the chat composer's Visual edits icon */
function CogoIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ pointerEvents: "none" }}>
      <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
      <path d="M20 3v4" />
      <path d="M22 5h-4" />
    </svg>
  )
}

/** X icon for close button */
function XIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M18 6L6 18M6 6l12 12" />
    </svg>
  )
}

/** Header with tool count from EmbeddedToolProvider */
function AgentHeader({
  appName,
  persona,
  onClose,
}: {
  appName?: string
  persona: AgentPersona
  onClose: () => void
}) {
  const { toolCount } = useEmbeddedTools()

  return (
    <div className="flex items-center justify-between px-4 py-3 border-b border-border">
      <div>
        <span className="text-sm font-semibold">
          {appName || "Cogo Agent"}
        </span>
        <span className="text-[10px] text-muted-foreground ml-2">
          {toolCount} tools available
        </span>
      </div>
      <button
        onClick={onClose}
        className="w-4 h-4 text-muted-foreground hover:text-foreground"
        aria-label="Close Agent panel"
        title="Close Agent panel"
      >
        <XIcon className="w-4 h-4" />
      </button>
    </div>
  )
}

/** Inner wrapper that reads AgentUI context and passes it to AgentPanel */
function AgentPanelWithContext({ persona }: { persona: { id: string; greeting: string } }) {
  const { pendingConfirmation, handleApprove, handleReject, streamingText } = useAgentUI()
  return (
    <AgentPanel
      persona={persona}
      onApprove={handleApprove}
      onReject={handleReject}
      pendingConfirmation={pendingConfirmation}
      streamingText={streamingText}
    />
  )
}

/** Loading skeleton per UI-SPEC */
function LoadingSkeleton() {
  return (
    <div className="p-4 space-y-3">
      <div className="h-4 bg-muted rounded animate-pulse w-full" />
      <div className="h-4 bg-muted rounded animate-pulse w-3/4" />
      <div className="h-4 bg-muted rounded animate-pulse w-1/2" />
    </div>
  )
}

export function CogoAgent({
  apiKey,
  appName,
  enabled = true,
  appId,
  llmDriver,
}: CogoAgentProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [persona, setPersona] = useState<AgentPersona | null>(null)
  const [embeddedEnabled, setEmbeddedEnabled] = useState<boolean | null>(null) // null = loading
  const [configPersona, setConfigPersona] = useState<string | null>(null)

  // Fetch agent_configs when appId is provided (D-18 embedded_enabled gate)
  useEffect(() => {
    if (!appId) {
      setEmbeddedEnabled(true) // No appId = Studio mode, always enabled
      return
    }
    const supabaseUrl = (window as any).__COGO_SUPABASE_URL__ || ''
    const anonKey = (window as any).__COGO_SUPABASE_ANON_KEY__ || ''
    if (!supabaseUrl) {
      // Try import.meta.env as fallback
      const envUrl = typeof import.meta !== 'undefined'
        ? (import.meta as any).env?.VITE_SUPABASE_URL
        : undefined
      const envKey = typeof import.meta !== 'undefined'
        ? (import.meta as any).env?.VITE_SUPABASE_ANON_KEY
        : undefined
      if (!envUrl) { setEmbeddedEnabled(false); return }

      // Dev mode: skip external agent-config fetch (avoids CORS errors with Supabase)
      if (envUrl && typeof import.meta !== 'undefined' && (import.meta as any).env?.DEV) {
        setEmbeddedEnabled(true)
        return
      }

      fetch(`${envUrl}/functions/v1/agent-config?app_id=${encodeURIComponent(appId)}`, {
        headers: { 'Content-Type': 'application/json', 'apikey': envKey || '' },
      })
        .then(r => r.ok ? (r.json() as Promise<{ embedded_enabled?: boolean; persona?: string } | null>) : null)
        .then(config => {
          if (config?.embedded_enabled) {
            setEmbeddedEnabled(true)
            if (config.persona) setConfigPersona(config.persona)
          } else {
            // Fallback: enable agent even if config not found (show FAB after login)
            setEmbeddedEnabled(true)
          }
        })
        .catch(() => setEmbeddedEnabled(true))
      return
    }

    fetch(`${supabaseUrl}/functions/v1/agent-config?app_id=${encodeURIComponent(appId)}`, {
      headers: { 'Content-Type': 'application/json', 'apikey': anonKey },
    })
      .then(r => r.ok ? (r.json() as Promise<{ embedded_enabled?: boolean; persona?: string } | null>) : null)
      .then(config => {
        if (config?.embedded_enabled) {
          setEmbeddedEnabled(true)
          if (config.persona) setConfigPersona(config.persona)
        } else {
          // Fallback: enable agent even if config not found (show FAB after login)
          setEmbeddedEnabled(true)
        }
      })
      .catch(() => setEmbeddedEnabled(true))
  }, [appId])

  // Detect persona from user role first, then fallback to tool-based detection
  useEffect(() => {
    // If config provided a persona, use the mapped persona object
    if (configPersona) {
      const PERSONA_MAP: Record<string, AgentPersona> = {
        shopping: shoppingPersona,
        dashboard: dashboardPersona,
        generic: genericPersona,
        partner: partnerPersona,
      }
      setPersona(PERSONA_MAP[configPersona] ?? genericPersona)
      return
    }

    let cancelled = false
    let attempts = 0
    const maxAttempts = 10

    function detect() {
      const cogo = (window as any).__cogo_tools__

      // Try role-based detection first
      const userRole = cogo?.getState?.("appData.auth.user.role") as string | undefined
      if (userRole && !cancelled) {
        const roleLower = userRole.toLowerCase()
        if (roleLower === "admin" || roleLower === "platform") {
          setPersona(dashboardPersona)
          return
        }
        if (roleLower === "store" || roleLower === "merchant") {
          setPersona(partnerPersona)
          return
        }
      }

      // Fallback: tool-based detection
      const tools = cogo?.getManifest?.() ?? []
      if (tools.length > 0 || attempts >= maxAttempts) {
        const hasCartTools = tools.some(
          (t: { name?: string }) => t.name?.includes("cart"),
        )
        const hasTableTools = tools.some(
          (t: { name?: string }) =>
            t.name?.includes("sort_column") ||
            t.name?.includes("data_table"),
        )
        if (!cancelled) {
          if (hasCartTools) setPersona(shoppingPersona)
          else if (hasTableTools) setPersona(dashboardPersona)
          else setPersona(genericPersona)
        }
      } else {
        attempts++
        setTimeout(detect, 200)
      }
    }

    detect()
    return () => { cancelled = true }
  }, [configPersona])

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsOpen(false)
    }
    window.addEventListener("keydown", handleEscape)
    return () => window.removeEventListener("keydown", handleEscape)
  }, [isOpen])

  if (!enabled) return null
  if (!apiKey || !llmDriver) return null
  // embedded_enabled gate: null = loading, false = disabled
  if (embeddedEnabled === null || embeddedEnabled === false) return null

  return (
    <EmbeddedToolProvider>
      {/* Floating Icon (FAB) */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-lg hover:bg-primary/90 transition-colors flex items-center justify-center"
          style={{ zIndex: 9999 }}
          aria-label="Open Agent Assistant"
          title="Open Agent Assistant"
        >
          <CogoIcon className="w-6 h-6" />
        </button>
      )}

      {/* Chat Panel */}
      {isOpen && persona && (
        <div
          role="dialog"
          aria-label="Cogo Agent Chat"
          className="fixed bottom-24 right-6 w-96 h-[600px] rounded-2xl border border-border bg-card shadow-2xl flex flex-col overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-200"
          style={{ zIndex: 9999 }}
        >
          {/* Header with app name and tool count */}
          <AgentHeader
            appName={appName}
            persona={persona}
            onClose={() => setIsOpen(false)}
          />

          {/* Thread (assistant-ui) -- lazy-loaded via AgentPanel */}
          <div className="flex-1 flex flex-col min-h-0">
            <AgentRuntimeProvider apiKey={apiKey} persona={persona} llmDriver={llmDriver}>
              <Suspense fallback={<LoadingSkeleton />}>
                <AgentPanelWithContext persona={persona} />
              </Suspense>
            </AgentRuntimeProvider>
          </div>
        </div>
      )}
    </EmbeddedToolProvider>
  )
}
